import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  WaveCRDTBridge,
  WaveSyncEngine,
  WaveCloudAPI,
  decodeSnapshotB64,
  WaveTemplateCatalog,
  looksLikePlaceholder,
  emptyWaveSyncDiagnostics,
  type WaveSyncDiagnostics,
  type WaveDocEditor,
  type WavePresenceRecord,
  type WaveRecord,
} from "@vida/wave-core";
import {
  createWaveApiConfig,
  ensureWaveCloudProfile,
  resetWaveSaveState,
  setRealtimeStatus,
  setSyncEmitError,
  setWaveSaveState,
} from "../services/waveSession";
import { currentUserSub } from "../auth";
import {
  getAppsyncEndpoint,
  getAppsyncRealtimeEndpoint,
  waitForEndpointReady,
} from "../config";
import { fetchWaveClientConfig } from "../services/waveClientConfig";
import { EMPTY_OPS, EMPTY_SNAPSHOT, clearIfNonEmpty } from "../utils/stableEmpty";

/** Sync + CRDT — montare solo quando una wave è aperta (WaveWorkspace). */
export function useWaveBridge(waveId: string, wave: WaveRecord) {
  const bridgeRef = useRef<WaveCRDTBridge | null>(null);
  if (!bridgeRef.current) bridgeRef.current = new WaveCRDTBridge();
  const bridge = bridgeRef.current;

  const [, bump] = useState(0);
  useEffect(() => bridge.subscribe(() => bump((n) => n + 1)), [bridge]);

  const syncRef = useRef<WaveSyncEngine | null>(null);
  const [syncEngineReady, setSyncEngineReady] = useState(false);
  const [documentReady, setDocumentReady] = useState(false);
  const [networkHydrating, setNetworkHydrating] = useState(false);
  const [documentRevision, setDocumentRevision] = useState(0);
  const [syncDiagnosticsRevision, setSyncDiagnosticsRevision] = useState(0);
  const [endpointReady, setEndpointReady] = useState(
    () => Boolean(getAppsyncEndpoint()) || import.meta.env.DEV,
  );

  useEffect(() => {
    if (getAppsyncEndpoint()) {
      setEndpointReady(true);
      return;
    }
    let cancelled = false;
    void waitForEndpointReady().then(() => {
      if (!cancelled) setEndpointReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!endpointReady || syncRef.current) return;
    let cancelled = false;
    void (async () => {
      await fetchWaveClientConfig(createWaveApiConfig());
      if (cancelled) return;
      const engine = new WaveSyncEngine(
        createWaveApiConfig(),
        getAppsyncRealtimeEndpoint(),
        {
          onStatusChange: setRealtimeStatus,
          onEmitError: setSyncEmitError,
          onSaveStateChange: setWaveSaveState,
          onDocumentChange: () => {
            setDocumentRevision((n) => n + 1);
            bump((x) => x + 1);
          },
        },
      );
      syncRef.current = engine;
      setSyncEngineReady(true);
      setRealtimeStatus(engine.getStatus());
    })();
    return () => {
      cancelled = true;
    };
  }, [endpointReady]);

  useEffect(() => () => {
    syncRef.current?.dispose();
    syncRef.current = null;
    setSyncEngineReady(false);
    setRealtimeStatus("offline");
    setSyncEmitError(null);
    resetWaveSaveState();
  }, []);

  const loadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!syncEngineReady || !endpointReady || wave.waveId !== waveId) return;
    const engine = syncRef.current;
    if (!engine) return;

    const loadKey = waveId;
    if (loadedKeyRef.current === loadKey) return;
    loadedKeyRef.current = loadKey;
    setDocumentReady(false);
    setNetworkHydrating(false);
    void (async () => {
      await engine.loadWave(bridge, wave);
      setDocumentRevision(engine.getDocumentRevision());
      setDocumentReady(true);
      setNetworkHydrating(engine.isNetworkHydrating());
      setRealtimeStatus(engine.getStatus());
      setWaveSaveState(engine.getSaveState(), engine.getSaveStateMeta());
      bump((n) => n + 1);
      if (
        looksLikePlaceholder(
          engine.getDocumentEditor()?.blocks ?? bridge.blocks,
          wave,
        ) &&
        (wave.nextOpSeq ?? 0) > 0
      ) {
        await engine.rehydrateDocument(bridge, wave);
        setDocumentRevision(engine.getDocumentRevision());
        bump((n) => n + 1);
      }
    })();

    const unsubHydration = engine.subscribeNetworkHydration(() => {
      setNetworkHydrating(engine.isNetworkHydrating());
      setDocumentRevision(engine.getDocumentRevision());
      setSyncDiagnosticsRevision((n) => n + 1);
      setRealtimeStatus(engine.getStatus());
      setWaveSaveState(engine.getSaveState(), engine.getSaveStateMeta());
      bump((n) => n + 1);
    });

    const unsubDiagnostics = engine.subscribeSyncDiagnostics(() => {
      setSyncDiagnosticsRevision((n) => n + 1);
    });

    return () => {
      unsubHydration();
      unsubDiagnostics();
      if (loadedKeyRef.current === loadKey) {
        loadedKeyRef.current = null;
        setDocumentReady(false);
        setNetworkHydrating(false);
      }
      engine.unloadWave(waveId);
    };
  }, [waveId, bridge, endpointReady, syncEngineReady]);

  const emitOps = useCallback(
    (opsB64: string[]) => {
      syncRef.current?.emitOps(waveId, bridge, opsB64);
    },
    [waveId, bridge],
  );

  const flushDocumentSync = useCallback(async () => {
    await syncRef.current?.flushDocumentSync(waveId);
  }, [waveId]);

  const editor = useMemo(
    () =>
      syncEngineReady && syncRef.current?.usesYjs()
        ? syncRef.current?.getDocumentEditor() ?? null
        : null,
    [syncEngineReady, documentRevision],
  );

  const usesYjs = editor != null;

  const syncDiagnostics = useMemo((): WaveSyncDiagnostics => {
    void syncDiagnosticsRevision;
    return syncRef.current?.getSyncDiagnostics() ?? emptyWaveSyncDiagnostics();
  }, [syncEngineReady, syncDiagnosticsRevision, documentRevision]);

  useEffect(() => {
    if (!usesYjs) return;
    const onPageHide = () => {
      syncRef.current?.prepareForUnload(waveId);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [waveId, usesYjs]);

  useEffect(() => {
    if (!usesYjs || !syncEngineReady) return;
    const onOnline = () => {
      syncRef.current?.notifyBrowserOnline();
    };
    const onOffline = () => {
      syncRef.current?.notifyBrowserOffline();
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [waveId, usesYjs, syncEngineReady]);

  return {
    bridge,
    emitOps,
    flushDocumentSync,
    syncEngine: syncEngineReady ? syncRef.current : null,
    endpointReady,
    editor,
    documentRevision,
    documentReady,
    networkHydrating,
    usesYjs,
    syncDiagnostics,
  };
}

export function useWaveRealtimeExtras(
  waveId: string,
  syncEngine: WaveSyncEngine | null,
  onStatus: (w: WaveRecord) => void,
  onPresence: (p: WavePresenceRecord) => void,
  presenceRetryKey = 0,
  presenceEnabled = true,
) {
  useEffect(() => {
    if (!syncEngine) return;
    const u1 = syncEngine.subscribeStatus(waveId, onStatus);
    return () => {
      u1();
    };
  }, [waveId, syncEngine, onStatus]);

  useEffect(() => {
    if (!syncEngine || !presenceEnabled) return;
    const u3 = syncEngine.subscribePresence(waveId, onPresence);
    return () => {
      u3();
    };
  }, [waveId, syncEngine, onPresence, presenceRetryKey, presenceEnabled]);
}

export function usePlaybackSource(wave: WaveRecord, enabled: boolean) {
  const [baseline, setBaseline] = useState(EMPTY_SNAPSHOT);
  const [ops, setOps] = useState(EMPTY_OPS);
  const waveId = wave.waveId;
  const snapshotSeq = wave.latestSnapshotSeq;
  const templateId = wave.templateId ?? null;
  const snapshotB64 = wave.latestSnapshotB64 ?? null;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      const cfg = createWaveApiConfig();
      const template = WaveTemplateCatalog.find(templateId);
      if (template) {
        if (!cancelled) {
          setBaseline({ blocks: template.blocks, version: 1 });
          setOps(await WaveCloudAPI.listAllOps(cfg, waveId));
        }
        return;
      }
      const snap = decodeSnapshotB64(snapshotB64);
      if (snap) {
        if (!cancelled) {
          setBaseline(snap);
          setOps(
            (await WaveCloudAPI.listOpsSince(cfg, waveId, snapshotSeq)).items,
          );
        }
        return;
      }
      if (!cancelled) {
        setBaseline(EMPTY_SNAPSHOT);
        setOps(await WaveCloudAPI.listAllOps(cfg, waveId));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [waveId, snapshotSeq, templateId, snapshotB64, enabled]);

  const refresh = useCallback(async () => {
    const cfg = createWaveApiConfig();
    const template = WaveTemplateCatalog.find(templateId);
    if (template) {
      setBaseline({ blocks: template.blocks, version: 1 });
      setOps(await WaveCloudAPI.listAllOps(cfg, waveId));
      return;
    }
    const snap = decodeSnapshotB64(snapshotB64);
    if (snap) {
      setBaseline(snap);
      setOps(
        (await WaveCloudAPI.listOpsSince(cfg, waveId, snapshotSeq)).items,
      );
      return;
    }
    setBaseline(EMPTY_SNAPSHOT);
    setOps(await WaveCloudAPI.listAllOps(cfg, waveId));
  }, [waveId, snapshotSeq, templateId, snapshotB64]);

  return { baseline, ops, refresh };
}

export function useWaveSpaces(isLoggedIn: boolean) {
  const [spaces, setSpaces] = useState<
    import("@vida/wave-core").WaveOceanSpaceRecord[]
  >([]);

  const loadSpaces = useCallback(async () => {
    if (!isLoggedIn) return;
    await ensureWaveCloudProfile();
    return WaveCloudAPI.listOceanSpaces(createWaveApiConfig());
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    void loadSpaces().then((items) => {
      if (!cancelled && items) setSpaces(items);
    });
    // Parità Tide: nessuna subscription sugli spazi, refresh on-focus.
    const onFocus = () => {
      void loadSpaces().then((items) => {
        if (items) setSpaces(items);
      });
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [isLoggedIn, loadSpaces]);

  const refreshSpaces = useCallback(async () => {
    const items = await loadSpaces();
    if (items) setSpaces(items);
  }, [loadSpaces]);

  return { spaces, setSpaces, refreshSpaces };
}

export function useWaveList(spaceId: string | null, isLoggedIn: boolean) {
  const [waves, setWaves] = useState<WaveRecord[]>([]);

  useEffect(() => {
    if (!spaceId || !isLoggedIn) {
      setWaves(clearIfNonEmpty);
      return;
    }
    let cancelled = false;
    void WaveCloudAPI.listWaves(createWaveApiConfig(), spaceId).then((items) => {
      if (!cancelled) setWaves(items);
    });
    return () => {
      cancelled = true;
    };
  }, [spaceId, isLoggedIn]);

  const refresh = useCallback(async () => {
    if (!spaceId || !isLoggedIn) return;
    setWaves(await WaveCloudAPI.listWaves(createWaveApiConfig(), spaceId));
  }, [spaceId, isLoggedIn]);

  return { waves, setWaves, refresh };
}

export function useParticipatingWaves(isLoggedIn: boolean) {
  const [sharedWaves, setSharedWaves] = useState<WaveRecord[]>([]);

  const loadShared = useCallback(async () => {
    if (!isLoggedIn) {
      setSharedWaves(clearIfNonEmpty);
      return;
    }
    await ensureWaveCloudProfile();
    setSharedWaves(await WaveCloudAPI.listParticipatingWaves(createWaveApiConfig()));
  }, [isLoggedIn]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isLoggedIn) {
        if (!cancelled) setSharedWaves(clearIfNonEmpty);
        return;
      }
      await ensureWaveCloudProfile();
      const items = await WaveCloudAPI.listParticipatingWaves(createWaveApiConfig());
      if (!cancelled) setSharedWaves(items);
    })();
    const onFocus = () => {
      void loadShared();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [isLoggedIn, loadShared]);

  const refresh = useCallback(async () => {
    await loadShared();
  }, [loadShared]);

  return { sharedWaves, setSharedWaves, refresh };
}

export function useSharedByMeWaves(isLoggedIn: boolean) {
  const [sharedByMeWaves, setSharedByMeWaves] = useState<WaveRecord[]>([]);

  const loadSharedByMe = useCallback(async () => {
    if (!isLoggedIn) {
      setSharedByMeWaves(clearIfNonEmpty);
      return;
    }
    const ownerSub = currentUserSub();
    if (ownerSub === "anonymous") {
      setSharedByMeWaves(clearIfNonEmpty);
      return;
    }
    await ensureWaveCloudProfile();
    setSharedByMeWaves(
      await WaveCloudAPI.listSharedByMeWaves(createWaveApiConfig(), ownerSub),
    );
  }, [isLoggedIn]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isLoggedIn) {
        if (!cancelled) setSharedByMeWaves(clearIfNonEmpty);
        return;
      }
      const ownerSub = currentUserSub();
      if (ownerSub === "anonymous") {
        if (!cancelled) setSharedByMeWaves(clearIfNonEmpty);
        return;
      }
      await ensureWaveCloudProfile();
      const items = await WaveCloudAPI.listSharedByMeWaves(
        createWaveApiConfig(),
        ownerSub,
      );
      if (!cancelled) setSharedByMeWaves(items);
    })();
    const onFocus = () => {
      void loadSharedByMe();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [isLoggedIn, loadSharedByMe]);

  const refresh = useCallback(async () => {
    await loadSharedByMe();
  }, [loadSharedByMe]);

  return { sharedByMeWaves, setSharedByMeWaves, refresh };
}

export function useOwnedWaves(isLoggedIn: boolean) {
  const [ownedWaves, setOwnedWaves] = useState<WaveRecord[]>([]);

  const loadOwned = useCallback(async () => {
    if (!isLoggedIn) {
      setOwnedWaves(clearIfNonEmpty);
      return;
    }
    const ownerSub = currentUserSub();
    if (ownerSub === "anonymous") {
      setOwnedWaves(clearIfNonEmpty);
      return;
    }
    await ensureWaveCloudProfile();
    setOwnedWaves(await WaveCloudAPI.listOwnedWaves(createWaveApiConfig(), ownerSub));
  }, [isLoggedIn]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await loadOwned();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOwned]);

  const refresh = useCallback(async () => {
    await loadOwned();
  }, [loadOwned]);

  return { ownedWaves, setOwnedWaves, refresh };
}

export function useMemoWaveApi() {
  return useMemo(() => createWaveApiConfig(), []);
}

export type { WaveDocEditor };
